import { CustomFieldType, IntakeFormSections } from '@ee/common/enums';
import { EVICTION_REASONS_CLAIMS_MAP } from '@ee/common/constants';
import { evictionReasonFieldFromOptions } from '@ee/common/forms';
import {
  CustomField,
  IntakeForm,
  IntakeFormSection,
  LAYOUT_COLUMNS,
  LAYOUT_VERSION_LEGACY,
  LAYOUT_VERSION_ROWS,
  LayoutRow,
  legacyColumnsForSpan,
  migrateFieldsToRows
} from '@ee/common/models';
import {
  FieldDefinitionView,
  LayoutPageView,
  LayoutSectionView,
  isIntakePlaceable,
  lockReasonFor,
  sizeHintForColumns
} from './intake-layout.model';
import { CompositeType, IntakeOption, PlacementLockReason } from '@ee/common/models';
import { COMPOSITE_LABELS, COMPOSITE_PART_KEYS } from './composites';

/**
 * Between the stored `IntakeForm` and what the builder edits.
 *
 * The adapter is where the mixed-version estate is absorbed: a v1 form is projected into rows so it can
 * be *shown* in the v2 canvas, but nothing is written back until the firm is actually migrated. Reading
 * and writing are separate functions for exactly that reason — `toPages` never mutates.
 */

/**
 * Page one of the public form, which is **not** stored anywhere.
 *
 * Its questions live in two hand-written components — `user-form.component.ts` and
 * `organization-form.component.ts` — not in `IntakeFormSection.fields`. Deriving the page from the data
 * therefore produced an empty Business Information and a Contact Information holding nothing but a
 * stray custom field that the v1 renderer never shows either. Declared here so the canvas states what
 * the form actually asks.
 *
 * Keep in step with those two components. The alternative — reading the fields out of the markup — is
 * not available, and inventing them from the section map is what went wrong the first time.
 */
const PUBLIC_FORM_NOTE =
  'These questions create the client\'s account, so they are fixed. You can still add a heading or ' +
  'help text around them.';

interface DeclaredCell {
  key: string;
  label: string;
  span: number;
  /** Required when v1 has no option to say otherwise. An option's `required` wins where there is one. */
  required?: boolean;
  /** So the preview draws the control the wizard draws, rather than a generic box. */
  type?: CustomFieldType;
  /** Choices for a select, where the wizard has a fixed list. */
  options?: string[];
  /**
   * The `IntakeOption` that gates this field in v1, when one does. Defaults to the field key.
   *
   * v2 has no `show`: a field is placed or it is not. So the option is read once, at projection, and
   * translated — `show: false` becomes *absent from the rows and offered in Add field*, and `required`
   * becomes the placement's own flag. After that the option is vestigial, the way `claims_preset` was.
   */
  option?: string;
  /** No option gates it in v1; the wizard always renders it. */
  always?: boolean;
  /** Cannot be removed or moved. The section is still editable around it. */
  locked?: boolean;
  /**
   * Placed as one block whose parts are never individually placeable.
   *
   * The `key` is then the block's own key rather than a field's — nothing writes to it — and the parts
   * in [COMPOSITE_PARTS] are what the wizard renders and stores.
   */
  composite?: CompositeType;
}

interface DeclaredSection {
  key: IntakeFormSections;
  title: string;
  rows: DeclaredCell[][];
  /** The sentence shown on the section. */
  note: string;
  /**
   * Whether a firm can add and remove these placements.
   *
   * False for the account pages, whose questions create the client's login and cannot be anything else.
   * True for the tenant step, where v1's `show` toggles were exactly this decision wearing a different
   * hat — so the toggles become presence, and the Add field picker is where a switched-off one comes
   * back from.
   */
  editable?: boolean;
  /**
   * Whether the section also holds fields that are not declared here.
   *
   * The account pages are wholly the product's, so anything stored against them that the declaration
   * does not know is a stale write and is dropped. Property is not like that: the wizard owns the
   * address block and the two questions above it, but the section is also where a firm's own property
   * fields live — a gate code, an APN, a property ID. Merging keeps them; replacing would delete them
   * off the form on first read.
   */
  merge?: boolean;
}

const PUBLIC_FORM_SECTIONS: DeclaredSection[] = [
  {
    key: IntakeFormSections.USER,
    title: 'Contact Information',
    note: PUBLIC_FORM_NOTE,
    rows: [
      [
        { key: 'first_name', label: 'First Name', span: 6, required: true, type: CustomFieldType.TEXT },
        { key: 'last_name', label: 'Last Name', span: 6, required: true, type: CustomFieldType.TEXT }
      ],
      [
        { key: 'email', label: 'Email Address', span: 6, required: true, type: CustomFieldType.TEXT },
        { key: 'phone_number', label: 'Phone Number', span: 6, required: true, type: CustomFieldType.TEXT }
      ]
    ]
  },
  {
    key: IntakeFormSections.ORGANIZATION,
    title: 'Business Information',
    note: PUBLIC_FORM_NOTE,
    rows: [
      // The filer's own company, on the page that creates their account. The *plaintiff* is a separate
      // field on page two — see the PLAINTIFF section below.
      [{ key: 'company_name', label: 'Company Name', span: 12, required: true, type: CustomFieldType.TEXT }],
      [{ key: 'street_address_1', label: 'Street Address', span: 12, required: true }],
      [{ key: 'street_address_2', label: 'Street Address 2', span: 12 }],
      [
        { key: 'city', label: 'City', span: 6, required: true },
        { key: 'state', label: 'State', span: 3, required: true, type: CustomFieldType.SELECT },
        { key: 'zip_code', label: 'Zip Code', span: 3, required: true }
      ]
    ]
  }
  ,
  {
    /*
     * The plaintiff.
     *
     * Keyed `plaintiff`, which is what every document template merges and what
     * `FillableFormFieldBuilder` resolves — from `eviction.companyName`. The built-in field annotation
     * on `Eviction` says `company_name` for the same thing, so the intake key and the merge field
     * disagree in the codebase; `plaintiff` is the one that is addressable from a template, so it is
     * the one the layout uses.
     *
     * Not to be confused with page one's `company_name`, which is the *filer's* own company on the
     * account-creation page.
     */
    key: IntakeFormSections.PLAINTIFF,
    title: 'Plaintiff',
    note: 'Who is filing. Merged into documents as #plaintiff#.',
    editable: true,
    rows: [
      [
        {
          key: 'plaintiff',
          label: 'Plaintiff Name',
          span: LAYOUT_COLUMNS,
          required: true,
          always: true,
          locked: true,
          type: CustomFieldType.TEXT
        }
      ]
    ]
  },
  {
    /*
     * The tenant party, and nothing else.
     *
     * `eviction-tenants-section.component.ts` has **no field loop at all** — every control in it is
     * hard-coded markup gated on an option — so the tenant accordion, the two declarations and the
     * eviction reason were all invisible to the layout. That is why the tenants never appeared on the
     * canvas: there was nothing in the data to draw.
     */
    key: IntakeFormSections.TENANT,
    title: 'Tenant Information',
    note:
      'Asked once per tenant — the wizard repeats these for each one. Add or remove them here; the ' +
      'ones your form used to switch off are in Add field.',
    editable: true,
    rows: [
      [
        {
          /*
           * Namespaced, because these are fields of a *tenant*, not of the case.
           *
           * The wizard collects them inside a per-tenant form group — the storage path is
           * `tenants[i].email` — and the account page has its own top-level `email`, `first_name`,
           * `last_name` and `phone_number` for the person filing. Sharing a key would have made the
           * registry, the Add-field picker and `placedKeys` treat the filer's email and a tenant's as
           * one field.
           */
          key: 'tenants.first_name',
          option: 'first_name',
          label: 'Tenant\'s First Name',
          span: 4,
          required: true,
          always: true,
          locked: true,
          type: CustomFieldType.TEXT
        },
        { key: 'tenants.middle_name', option: 'middle_name', label: 'Tenant\'s Middle Name', span: 4, type: CustomFieldType.TEXT },
        {
          key: 'tenants.last_name',
          option: 'last_name',
          label: 'Tenant\'s Last Name',
          span: 4,
          required: true,
          always: true,
          locked: true,
          type: CustomFieldType.TEXT
        }
      ],
      [
        { key: 'tenants.phone_number', option: 'phone_number', label: 'Phone Number', span: 4, type: CustomFieldType.TEXT },
        { key: 'tenants.email', option: 'email', label: 'Email Address', span: 4, type: CustomFieldType.TEXT },
        { key: 'tenants.dob', option: 'dob', label: 'Date of Birth', span: 4, type: CustomFieldType.DATE }
      ],
      [
        { key: 'tenants.ssn', option: 'ssn', label: 'SSN', span: 4, type: CustomFieldType.TEXT },
        {
          key: 'tenants.is_not_active_military_member',
          option: 'is_not_active_military_member',
          label: 'Not an active military member',
          span: 8,
          type: CustomFieldType.BOOLEAN
        }
      ]
    ]
  },
  {
    /*
     * Everything else the tenant step asks.
     *
     * v1 renders these inside Tenant Information; they are a section of their own here because none of
     * them is about who the tenants are — they are why the tenancy is ending and what has been declared
     * about it.
     */
    key: IntakeFormSections.EVICTION_REASON,
    title: 'Eviction Reason',
    note: 'Asked on the tenant step. The list of reasons itself is configured on the form, not here.',
    editable: true,
    rows: [
      [
        {
          key: 'eviction_reason',
          label: 'Eviction Reason',
          span: 3,
          required: true,
          locked: true,
          /*
           * A radio group, unless the firm asked for checkboxes.
           *
           * Legacy null / SELECT / RADIO is a radio group and MULTI_SELECT is checkboxes — the same
           * reading as `evictionReasonFieldFromOptions`. The cell type here is only the product
           * default; `toDefinitions` replaces it from the firm's option so a checkbox firm is not
           * drawn as a single select. The list itself is the wizard's default; a firm's own
           * `options['eviction_reason'].options` wins.
           */
          type: CustomFieldType.RADIO,
          options: [...EVICTION_REASONS_CLAIMS_MAP.keys()]
        },
        { key: 'reason', label: 'Other Reason Description', span: 9, type: CustomFieldType.TEXT }
      ],
      [
        {
          key: 'not_active_military',
          label: 'Tenants are not active military',
          span: LAYOUT_COLUMNS,
          type: CustomFieldType.BOOLEAN
        }
      ],
      [
        {
          key: 'applied_for_assistance',
          label: 'Applied for rental assistance',
          span: LAYOUT_COLUMNS,
          type: CustomFieldType.BOOLEAN
        }
      ]
    ]
  },
  {
    /*
     * The property step's own questions.
     *
     * `eviction-property-section.component.ts` is hand-written markup gated on options, exactly like the
     * tenant step — so the address, the property name and the property type were invisible to the
     * layout, and the canvas showed a Property section containing nothing but the firm's own custom
     * fields. Declared here for the same reason page one is.
     *
     * Merged rather than replacing: this section is also where a firm's property fields live.
     */
    key: IntakeFormSections.PROPERTY,
    title: 'Property',
    note:
      'The address is asked as one question — the autocomplete fills the rest of it, and the county ' +
      'comes from the ZIP. Which unit types it offers is yours to set.',
    editable: true,
    merge: true,
    rows: [
      [
        { key: 'property_name', label: 'Property Name', span: 6, type: CustomFieldType.TEXT },
        {
          /*
           * A radio in the wizard, and the three choices it hard-codes. A firm that had switched it off
           * in v1 does not get it placed — `show` becomes presence here as everywhere else — and it
           * comes back the ordinary way, from Add field.
           */
          key: 'property_type',
          label: 'Property Type',
          span: 6,
          type: CustomFieldType.RADIO,
          options: ['Conventional', 'Public Housing', 'Section 8']
        }
      ],
      [
        {
          /*
           * One question with seven parts, and the only one of them a firm can change is which unit
           * types the list offers. The autocomplete writes street, city, state and ZIP from a single
           * selection and the county is resolved from the ZIP, so a part placed on its own would be a
           * control the wizard overwrites.
           */
          key: 'property_address',
          label: COMPOSITE_LABELS.ADDRESS,
          span: LAYOUT_COLUMNS,
          required: true,
          always: true,
          locked: true,
          composite: 'ADDRESS'
        }
      ]
    ]
  }
];

/**
 * Why every field on page one is locked: they create the client's account, so the form cannot ask for
 * anything else in their place, and it cannot ask for them anywhere else.
 */
const PUBLIC_FORM_LOCK: PlacementLockReason = 'CLIENT_CREATION';


/** The order the fixed section map is laid out in today, and the pages they fall on. */
const DEFAULT_PAGES: { id: string; name: string; publicOnly?: boolean; fixed?: boolean; sections: IntakeFormSections[] }[] = [
  {
    id: 'page-account',
    // Named for what it is to the person filling it in, and matching the live form's own first two
    // sections. "Your account" was invented here and corresponded to nothing on the real form.
    name: 'Getting started',
    // The public form asks the account questions because the filer has no account; the portal already
    // knows who they are and skips the page entirely.
    publicOnly: true,
    fixed: true,
    sections: [IntakeFormSections.USER, IntakeFormSections.ORGANIZATION]
  },
  {
    id: 'page-parties',
    name: 'Parties & property',
    // Eviction Reason sits under the tenants on today's form, not with the claims — it is a fact about
    // why this tenancy is ending, which is why it is asked where the tenancy is described.
    sections: [
      IntakeFormSections.PLAINTIFF,
      IntakeFormSections.TENANT,
      IntakeFormSections.EVICTION_REASON,
      IntakeFormSections.PROPERTY
    ]
  },
  {
    id: 'page-claims',
    // Claims and Lease Terms, as today's form groups them — the lease is what the claim is measured
    // against. Documents were never on this page.
    name: 'Claims & lease terms',
    sections: [IntakeFormSections.CLAIMS, IntakeFormSections.LEASE_TERMS]
  },
  {
    id: 'page-documents',
    name: 'Documents',
    /*
     * Documents only. Other Information and Internal Fields are deliberately not intake pages.
     *
     * Neither was ever a set of questions for a client. In v1, putting a field in one of them was the
     * only way to make it appear on the case sidebar, so they filled up with fields that exist to be
     * *seen by staff* rather than asked at intake. The sidebar has its own layout now, so the workaround
     * has somewhere real to go and the sections have no reason to be on the form.
     *
     * Dropping them from the pages is not destructive: `applyPages` only writes sections that appear on
     * a page, so their stored fields are left exactly as they are and stay available to the sidebar and
     * the registry.
     */
    sections: [IntakeFormSections.DOCUMENTS]
  }
];

/** True for a section whose questions the product owns — page one of the public form. */
export function isPublicFormSection(key: string): boolean {
  // Compared as strings: `key` is a section key off the document, which is an enum value only by
  // convention — a custom section's key is whatever its author typed.
  return PUBLIC_FORM_SECTIONS.some(section => String(section.key) === key);
}

/**
 * True for a declared section that also holds the firm's own fields.
 *
 * It is declared, so it is projected; but it is not wholly the product's, so it is written back like an
 * ordinary section. Storing only the four keys the product-owned branch keeps would drop a firm's custom
 * property fields on the first save.
 */
export function isMergedFormSection(key: string): boolean {
  return PUBLIC_FORM_SECTIONS.some(section => String(section.key) === key && section.merge === true);
}

/**
 * The fixed page-one section: the product's questions, arranged by whatever the firm has authored around
 * them.
 *
 * The **structure** is stored, the **fields** are not. So the saved rows decide where a heading sits
 * relative to the questions, while every field item is re-read from the declaration — which means a
 * heading dragged between two rows stays there, and nothing about the questions themselves can go stale
 * or be corrupted by an edit.
 *
 * Reconciled rather than trusted: a stored field the declaration no longer has is dropped, and a declared
 * field the stored rows have lost is put back in its canonical row. A form saved before a question was
 * added or renamed therefore repairs itself on read instead of quietly asking the wrong thing.
 */
function publicFormSection(
  stored: IntakeFormSection | undefined,
  index: number,
  options: Record<string, IntakeOption> = {}
): LayoutSectionView {
  const declared = PUBLIC_FORM_SECTIONS[index];
  const declaredByKey = new Map(declared.rows.flat().map(cell => [cell.key, cell]));

  /*
   * v1's `show` becomes presence.
   *
   * There is no "hidden but configured" in v2 — a field is placed or it is offered in Add field — so the
   * option is read once here and never again. A cell the firm had switched off simply does not appear,
   * and comes back the ordinary way. `required` becomes the placement's own flag for the same reason.
   */
  const shown = (cell: DeclaredCell): boolean =>
    cell.always === true || options[cell.option ?? cell.key]?.show !== false;

  const requiredFor = (cell: DeclaredCell): boolean =>
    options[cell.option ?? cell.key]?.required ?? cell.required ?? false;

  /*
   * The expanded hint, which v1 renders as "Hover for more info" beside a claims or tenant field.
   *
   * Two stores hold the same sentence. `section.localization['<key>_hint']` is the one the wizard
   * reads and the localization editor writes; `options[key].hint` is the older one it was seeded from,
   * and no component reads it any more. Both are consulted, newest first, so nothing a firm typed is
   * left behind — 39 options and a dozen localizations carry one in a sample estate.
   */
  const hintFor = (cell: DeclaredCell): string | null =>
    stored?.localization?.[`${cell.key}_hint`] || options[cell.option ?? cell.key]?.hint || null;

  const cellToItem = (cell: DeclaredCell) => ({
    kind: cell.composite ? ('COMPOSITE' as const) : ('FIELD' as const),
    field_key: cell.key,
    composite_type: cell.composite ?? null,
    span: cell.span,
    hint: hintFor(cell),
    /*
     * The link beside a question, which v1 offers on the rental-assistance declaration and renders as an
     * info icon. Eighteen forms in a sample estate set one, most of them to their state's ERAP page.
     */
    more_info_link: options[cell.option ?? cell.key]?.more_info_link || null,
    /*
     * The label the component actually renders, carried on the placement.
     *
     * Declared and then not used, these fell back to the registry definition — so a firm that had
     * relabelled `company_name` saw Business Information ask for "Plaintiff Name". The definition is the
     * wrong source here: page one is markup, and what it prints is not the registry's to change.
     *
     * It rides on `label_override` because that is where a per-surface label lives, but the tile does
     * not mark a locked placement as overridden — nobody chose this, it is simply what the page says.
     *
     * A firm's own wording wins over the declaration's. v1 lets one be set per question and renders it
     * on the rental-assistance declaration; eighteen forms have renamed that one, and taking the
     * declared label unconditionally put the product's words back in their mouths.
     */
    label_override: options[cell.option ?? cell.key]?.label_override || cell.label,
    required_intake: requiredFor(cell),
    // An editable section locks only the cells v1 always renders; a fixed one locks everything.
    lock_reason: declared.editable ? (cell.locked ? 'PRODUCT_OWNED' : null) : PUBLIC_FORM_LOCK
  });

  const canonical = () =>
    declared.rows
      .map((cells, rowIndex) => ({
        id: `${String(declared.key).toLowerCase()}-row-${rowIndex}`,
        items: cells.filter(shown).map(cellToItem)
      }))
      .filter(row => row.items.length);

  /*
   * A composite owns its parts, so a part can never also be a placement of its own.
   *
   * This is a migration guard, not a theoretical one. A firm that has been through the v2 migration has
   * `property_street_address` and `property_city` in its stored rows, because until the address was
   * declared they were ordinary fields — and passing them through beside the block would put two
   * editors on one storage key, which is the thing the composite exists to prevent.
   */
  const ownedByComposite = (item: { field_key?: string | null }): boolean =>
    !!item.field_key && COMPOSITE_PART_KEYS.has(item.field_key);

  const storedRows = stored?.rows ?? [];
  if (!storedRows.length) {
    /*
     * No stored rows.
     *
     * On a wholly product-owned section that is the whole answer. On a merged one it is the *legacy*
     * case — a firm that has never been migrated — and its own property fields are still in
     * `fields` / `built_in_fields`, never having been packed into rows. Returning the declaration alone
     * would show that firm a Property section with their gate code and property ID missing, which is
     * exactly what the un-migrated canvas exists to be honest about.
     */
    if (!declared.merge) {
      return view(canonical());
    }
    const own = migrateFieldsToRows(
      dedupeByKey([...(stored?.built_in_fields ?? []), ...(stored?.fields ?? [])]).filter(
        field => !declaredByKey.has(field.field) && !COMPOSITE_PART_KEYS.has(field.field)
      )
    );
    return view([...canonical(), ...own]);
  }

  const seen = new Set<string>();
  const rows = storedRows
    .map(row => ({
      id: row.id,
      items: row.items
        .map(item => {
          if (!item.field_key) {
            return item;
          }
          const cell = declaredByKey.get(item.field_key);
          if (!cell) {
            /*
             * Not one of the wizard's questions. On a merged section that is the firm's own field and it
             * passes through untouched; anywhere else it is a stale write against a key the declaration
             * no longer has, and keeping it would put a question on the form that nothing asks.
             *
             * Unless the composite now owns it — see `ownedByComposite`. Dropping it is not a loss: the
             * block places the same storage key, so the question is still asked and the data still lands
             * in the same place.
             */
            return declared.merge && !ownedByComposite(item) ? item : null;
          }
          if (seen.has(cell.key)) {
            return null;
          }
          seen.add(cell.key);
          /*
           * Label, type and lock always come from the declaration — they are the wizard's. Width and
           * requiredness come from the stored placement where there is one, because on an editable
           * section those are the firm's edits, and re-deriving them from the option would undo a
           * resize or a Required toggle on the next load.
           */
          /*
           * A locked cell keeps the declared width too, not just the declared label and type.
           *
           * It offers no resize handle, so a stored width for one cannot be a firm's edit — it is drift
           * from a seeder or a migrator. The seeded eviction reason had it at 12 beside a 12-wide Other
           * Reason Description, which is a 24-column row a firm could not fix from the canvas: the one
           * placement that would have to give way is the locked one.
           */
          return declared.editable
            ? {
              ...cellToItem(cell),
              span: cell.locked ? cell.span : (item.span ?? cell.span),
              required_intake: item.required_intake ?? requiredFor(cell)
            }
            : cellToItem(cell);
        })
        .filter((item): item is NonNullable<typeof item> => !!item)
    }))
    .filter(row => row.items.length);

  /*
   * Anything the stored rows lost comes back.
   *
   * On a fixed section that is every declared cell — a question added since the save. On an editable one
   * a missing cell is usually a removal the firm made, so only the **locked** ones return: those cannot
   * be removed, so their absence is never a decision.
   */
  /*
   * On a merged section, absence cannot mean "removed" until a declared cell has been stored at least
   * once. No form has ever held the property questions — they were markup — so on first read every shown
   * cell is placed. Once one of them is in the stored rows the section has been authored in v2 terms and
   * the ordinary rule takes over: only the locked ones come back.
   */
  const neverAuthored = declared.merge === true && seen.size === 0;

  const missing = canonical()
    .map(row => ({
      ...row,
      items: row.items.filter(
        item =>
          !seen.has(item.field_key) &&
          (!declared.editable || neverAuthored || declaredByKey.get(item.field_key)?.locked)
      )
    }))
    .filter(row => row.items.length);

  /*
   * Stored rows that resolve to nothing are a mismatch, not an empty section.
   *
   * Every editable declared section has at least one locked cell, so it cannot legitimately end up with
   * no declared fields. Rows written against keys this build no longer knows — a rename, or a restore
   * from an older document — would otherwise render the section blank with no way back.
   *
   * Guarded on the stored rows having *had* field keys: a section holding nothing but an authored
   * heading has no declared fields either, and resetting that would throw the heading away.
   */
  const storedFieldKeys = storedRows.flatMap(row => row.items).filter(item => !!item.field_key);
  if (!declared.merge && storedFieldKeys.length && !seen.size) {
    return view(canonical());
  }

  /*
   * The wizard's questions lead a merged section. Appending them would put the address — the thing the
   * property step exists to ask — underneath whatever custom fields the firm happens to have added.
   */
  return view(declared.merge ? [...missing, ...rows] : [...rows, ...missing]);

  function view(built: { id: string; items: unknown[] }[]): LayoutSectionView {
    return {
      key: String(declared.key),
      title: declared.title,
      rows: repackOverfullRows(built as LayoutSectionView['rows']),
      firm_only: false,
      fields_owned_by_product: !declared.editable,
      // A merged section shows keys: most of what it holds is the firm's own fields, and the key is how
      // they are identified everywhere else. Only a wholly product-owned page has nothing to identify.
      hide_field_keys: !declared.merge,
      locked_reason: declared.note,
      closed_to_moves: !declared.merge
    };
  }
}

/** The note the documents step collects, rendered by the wizard rather than stored as a field. */
export const DOCUMENTS_NOTE_FIELD = 'note';

/** Its own section, so it reads as a separate ask rather than as one more upload row. */
export const NOTES_SECTION_KEY = 'NOTES';

/**
 * The Notes section of the documents page.
 *
 * The wizard renders a "Notes for the attorney" panel right after the uploads — hard-coded markup, and
 * its own panel, not a row inside the documents list. So it is its own section here too: a free-text
 * note to the firm is a different kind of ask from a file to upload, and folding it into the upload
 * list made it read as one more document.
 *
 * Fixed throughout: the section cannot be renamed or removed and the placement cannot be moved, because
 * it is part of what the step is for rather than something a firm arranged.
 */
function notesSection(): LayoutSectionView {
  return {
    key: NOTES_SECTION_KEY,
    title: 'Notes',
    rows: [
      {
        id: 'documents-note-row',
        items: [
          {
            kind: 'FIELD',
            field_key: DOCUMENTS_NOTE_FIELD,
            span: LAYOUT_COLUMNS,
            label_override: null,
            required_intake: false,
            lock_reason: 'PRODUCT_OWNED'
          }
        ]
      }
    ],
    firm_only: false,
    content_owned_by_product: true,
    full_width_only: true,
    hide_field_keys: true,
    locked_reason: 'A note to your firm, asked at the end of the upload step. Its wording is ours.'
  };
}

/** Sections whose contents staff own and the client never sees. */
const FIRM_ONLY_SECTIONS: IntakeFormSections[] = [IntakeFormSections.INTERNAL];

export function sectionKeyOf(key: string, section: IntakeFormSection): string {
  return section.key || key;
}

/**
 * Project a form onto pages the builder can edit.
 *
 * A v1 section is *shown* as rows — computed with the same greedy pack the server uses — without being
 * written back. That is what lets a firm on v1 open the new canvas and see their form laid out correctly
 * before anyone commits them to a migration.
 */
export function toPages(form: IntakeForm): LayoutPageView[] {
  const sections = sectionMap(form);

  if (form.pages?.length) {
    return form.pages.map(page => ({
      id: page.id,
      name: page.name,
      public_only: page.public_only,
      fixed: page.fixed,
      post_submit: page.post_submit,
      sections: page.section_keys.map(key => sections.get(key)).filter((s): s is LayoutSectionView => !!s)
    }));
  }

  const pages: LayoutPageView[] = DEFAULT_PAGES.map(page => ({
    id: page.id,
    name: page.name,
    public_only: page.publicOnly,
    fixed: page.fixed,
    sections: page.sections.map(key => sections.get(key)).filter((s): s is LayoutSectionView => !!s)
  })).filter(page => page.sections.length);

  // The documents page carries the Notes section alongside the uploads.
  pages
    .filter(page => page.id === 'page-documents' && !page.sections.some(s => s.key === NOTES_SECTION_KEY))
    .forEach(page => page.sections.push(sections.get(NOTES_SECTION_KEY)));

  // Review is fixed and always last; it is generated rather than authored, so it holds no sections.
  pages.push({
    id: 'page-review',
    name: 'Review & file',
    fixed: true,
    note:
      'Everything entered so far is summarised here for the client to check before filing. It is built ' +
      'from the pages above, so there is nothing to lay out — change a page and this follows.',
    sections: []
  });
  return pages;
}

function sectionMap(form: IntakeForm): Map<string, LayoutSectionView> {
  const map = new Map<string, LayoutSectionView>();
  const raw = form.sections as unknown as Record<string, IntakeFormSection>;

  Object.keys(raw ?? {}).forEach(key => {
    const section = raw[key];
    if (!section) return;
    map.set(sectionKeyOf(key, section), toSectionView(key, section));
  });

  // Page one is declared, not read: its questions live in two components rather than in the data, so
  // whatever the section map holds for them is not what the form asks.
  // The form's options, read once and translated into presence — see `publicFormSection`.
  const options = (form.options ?? {}) as unknown as Record<string, IntakeOption>;
  PUBLIC_FORM_SECTIONS.forEach((declared, index) =>
    map.set(declared.key, publicFormSection(raw?.[declared.key], index, options))
  );

  // Same for Notes. It is in the map rather than only appended to the default page so that a form whose
  // pages have been saved resolves it too — otherwise the section disappeared the moment a firm's own
  // page list existed, which is every firm after their first edit.
  map.set(NOTES_SECTION_KEY, notesSection());

  (form.custom_sections ?? []).forEach(section => {
    const key = section.key ?? section.title;
    if (key) map.set(key, toSectionView(key, section));
  });

  return map;
}

/**
 * Move what will not fit onto a row of its own.
 *
 * A row asking for more than twelve columns cannot be rendered — the grid has twelve — so the canvas was
 * showing a layout the form could never produce, and blocking Publish over it. Worse, it was not always
 * fixable by hand: a locked placement offers no resize, so a row of two twelves where one of them is
 * locked has nothing a firm is allowed to shrink.
 *
 * Order is preserved and nothing is dropped or resized; the overflow simply starts the next row, which
 * is what the greedy pack the migrator runs would have produced in the first place.
 */
function repackOverfullRows(rows: LayoutRow[]): LayoutRow[] {
  if (!rows.some(row => row.items.reduce((total, item) => total + (item.span ?? 0), 0) > LAYOUT_COLUMNS)) {
    return rows;
  }

  const packed: LayoutRow[] = [];
  rows.forEach(row => {
    let current: LayoutRow = { ...row, items: [] };
    let used = 0;
    row.items.forEach(item => {
      const span = item.span ?? LAYOUT_COLUMNS;
      if (current.items.length && used + span > LAYOUT_COLUMNS) {
        packed.push(current);
        // Suffixed rather than reusing the id: two rows sharing one id breaks `track row.id` on the
        // canvas, which is how a drag ends up applying to the wrong row.
        current = { ...row, id: `${row.id}-${packed.length}`, items: [] };
        used = 0;
      }
      current.items.push(item);
      used += span;
    });
    packed.push(current);
  });
  return packed;
}

function dedupeByKey(fields: CustomField[]): CustomField[] {
  const seen = new Set<string>();
  return fields.filter(field => {
    const key = field.field;
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * The Documents section, where a repeated storage key is not a duplicate.
 *
 * Everywhere else on the form, one key means one question — placing a field twice would give two
 * editors one storage key. Documents are the exception by design: `field` on an upload is the
 * *attachment type*, and a form routinely asks for several of the same type. Nine of the twenty-nine
 * forms in a sample estate do; one asks for eight uploads across four types, three of them labelled
 * "Other Document". So a document slot's name is the whole of its identity to the client, and it lives
 * on the entry rather than on the type.
 *
 * That is what made three different uploads read as "Lease Agreement" three times over: the tile fell
 * back to the one definition the type has, and Lease Addenda and HAP Contract had nowhere to put their
 * own names.
 *
 * A form saved before this existed has rows carrying no label at all, so the names are matched back on
 * read — in order, per type, from the entries in `fields`. Within a type that is a best-effort
 * reconstruction: the rows have been dragged around since, and nothing in them records which entry
 * each came from. It is right for the common case, it is right about *which* names the section has,
 * and the first save pins whatever the firm sees.
 */
function documentRows(section: IntakeFormSection, stored: LayoutRow[] | null): LayoutRow[] {
  /*
   * Deduped across the two arrays, never within one.
   *
   * The same slot listed in both `built_in_fields` and `fields` is one upload written down twice —
   * that is what made Documents repeat itself. Two entries of one type inside `fields` are two
   * different uploads. Type alone cannot tell those apart, so the pair does: same type *and* same name
   * in both arrays is the duplicate, and the firm's own list wins.
   */
  const builtIn = section.built_in_fields ?? [];
  const own = section.fields ?? [];
  const pair = (field: CustomField) => `${field.field}\u0000${field.label ?? ''}`;
  const ownPairs = new Set(own.map(pair));
  const slots = [...builtIn.filter(field => !ownPairs.has(pair(field))), ...own];

  if (!stored) {
    // One row per slot: an upload is full width, so there is never more than one to a row.
    return slots.map((slot, index) => ({
      id: `document-row-${index}`,
      items: [
        {
          kind: 'FIELD' as const,
          field_key: slot.field,
          span: LAYOUT_COLUMNS,
          label_override: slot.label ?? null,
          hint: slot.hint ?? null,
          required_intake: slot.required ?? false
        }
      ]
    }));
  }

  // One queue per attachment type, drained in the order the rows appear.
  const unclaimed = new Map<string, CustomField[]>();
  slots.forEach(slot => {
    const queue = unclaimed.get(slot.field) ?? [];
    queue.push(slot);
    unclaimed.set(slot.field, queue);
  });

  return stored.map(row => ({
    ...row,
    items: row.items.map(item => {
      if (!item.field_key || item.label_override) {
        return item;
      }
      const slot = unclaimed.get(item.field_key)?.shift();
      return slot ? { ...item, label_override: slot.label ?? null, hint: item.hint ?? slot.hint ?? null } : item;
    })
  }));
}

export function toSectionView(key: string, section: IntakeFormSection): LayoutSectionView {
  const documents = sectionKeyOf(key, section) === String(IntakeFormSections.DOCUMENTS);

  const stored = section.layout_version === LAYOUT_VERSION_ROWS && section.rows?.length ? section.rows : null;

  /*
   * A v1 section is shown as rows but not written back. `migrateFieldsToRows` is the same greedy pack
   * the server runs, so what the canvas shows is what the migration would produce.
   *
   * Deduped by key. A section that holds the same field in both `built_in_fields` and `fields` — or
   * twice in one of them — otherwise projects two independently-movable tiles for one placement.
   * Documents are the exception and have their own projection: a repeated key there is a second
   * upload, not a duplicate.
   */
  const legacyRows = () =>
    migrateFieldsToRows(dedupeByKey([...(section.built_in_fields ?? []), ...(section.fields ?? [])]));

  const rows = documents ? documentRows(section, stored) : stored ? repackOverfullRows(stored) : legacyRows();

  /*
   * The expanded hint an ordinary section carries.
   *
   * Claims and Lease Terms are stored sections, so their built-in questions never went through the
   * declaration — but v1 still renders "Hover for more info" beside them from
   * `localization['<key>_hint']`, and v2 read neither store. The placement's own hint wins where one
   * has been authored since.
   */
  const localized = (item: LayoutRow['items'][number]) =>
    item.field_key ? (section.localization?.[`${item.field_key}_hint`] ?? null) : null;

  return {
    key: sectionKeyOf(key, section),
    title: section.title,
    description: section.description,
    /*
     * An upload row is a file picker and a filename; there is nothing in it that reads better at a
     * quarter width, and a half-width one just truncates the name. So documents are full width and the
     * resize handle is not offered — the control existed and did nothing anyone wanted.
     */
    full_width_only: documents,
    // `LEASE_AGREEMENT` and friends are AttachmentType names, not something a firm named or can use.
    hide_field_keys: documents,
    // A row that held nothing but an unplaceable field disappears with it, rather than rendering empty.
    rows: rows
      .map(row => ({
        id: row.id,
        items: row.items
          .filter(item => !item.field_key || isIntakePlaceable(item.field_key))
          .map(item => ({
            ...item,
            span: documents && !!item.field_key ? LAYOUT_COLUMNS : item.span,
            hint: item.hint ?? localized(item),
            lock_reason: item.lock_reason ?? lockReasonFor(item.field_key)
          }))
      }))
      .filter(row => row.items.length),
    firm_only: section.firm_only ?? FIRM_ONLY_SECTIONS.includes(key as IntakeFormSections)
  };
}

/**
 * Write the edited pages back onto the form.
 *
 * Only rows, pages and the layout version are touched. Definitions, bindings, required flags, rules and
 * merge fields are left exactly as they were, which is the same promise the server-side migrator makes —
 * and the reason a v1 firm can be shown this canvas without being migrated by accident.
 */
export function applyPages(form: IntakeForm, pages: LayoutPageView[]): IntakeForm {
  const raw = form.sections as unknown as Record<string, IntakeFormSection>;
  const byKey = new Map<string, IntakeFormSection>();
  Object.keys(raw ?? {}).forEach(key => byKey.set(sectionKeyOf(key, raw[key]), raw[key]));
  (form.custom_sections ?? []).forEach(section => {
    const key = section.key ?? section.title;
    if (key) byKey.set(key, section);
  });

  pages.forEach(page =>
    page.sections.forEach(view => {
      // The arrangement of page one is the firm's and is saved; the questions on it are the product's
      // and are not. A field is stored as its key alone, so a heading keeps the position it was dragged
      // to, while no copy of a span, label or requiredness the components own is persisted to go stale.
      // Synthesised, with no stored section behind it: the wizard owns both the question and its
      // wording, and there is nothing here a firm can have changed.
      if (view.key === NOTES_SECTION_KEY) {
        return;
      }
      // Declared, but not wholly the product's — written back whole, so the firm's own property fields
      // survive the save. What the declaration owns is re-derived on read regardless.
      if (isPublicFormSection(view.key) && !isMergedFormSection(view.key)) {
        const owned = byKey.get(view.key);
        if (owned) {
          // The arrangement is the firm's; the question is the wizard's. So a field is stored as its key
          // plus what a firm can change about it — where it sits, how wide, and whether it is required —
          // and never a copy of the label or type, which would go stale the moment the wizard changed.
          owned.rows = view.rows.map(row => ({
            id: row.id,
            items: row.items.map(item =>
              item.field_key
                ? {
                  kind: item.kind,
                  field_key: item.field_key,
                  span: item.span,
                  required_intake: item.required_intake
                }
                : item
            )
          }));
        }
        return;
      }
      const section = byKey.get(view.key);
      if (!section) return;
      section.title = view.title;
      section.rows = view.rows;
      section.layout_version = LAYOUT_VERSION_ROWS;
      section.firm_only = view.firm_only;
      // The derived width, kept for one release so anything still reading v1 keeps working. Rows and
      // spans are the source of truth; this is never authored.
      view.rows.forEach(row => row.items.forEach(item => (item.derived_columns = legacyColumnsForSpan(item.span))));
    })
  );

  form.pages = pages.map(page => ({
    id: page.id,
    name: page.name,
    public_only: page.public_only,
    fixed: page.fixed,
    post_submit: page.post_submit,
    section_keys: page.sections.map(section => section.key)
  }));
  form.layout_version = LAYOUT_VERSION_ROWS;
  return form;
}

/** True while any section is still on the legacy layout — the firm has not been migrated yet. */
export function isLegacyForm(form: IntakeForm): boolean {
  return (form.layout_version ?? LAYOUT_VERSION_LEGACY) < LAYOUT_VERSION_ROWS;
}

function sectionsOf(form: IntakeForm): IntakeFormSection[] {
  const raw = form.sections as unknown as Record<string, IntakeFormSection> | undefined;
  return [...Object.values(raw ?? {}), ...(form.custom_sections ?? [])].filter(section => !!section);
}

/** Field keys sitting on a stored row. Content blocks have none. */
function placedFieldKeys(form: IntakeForm): Set<string> {
  const keys = new Set<string>();
  sectionsOf(form).forEach(section =>
    section.rows?.forEach(row =>
      row.items?.forEach(item => {
        if (item.kind === 'FIELD' && item.field_key) {
          keys.add(item.field_key);
        }
      })
    )
  );
  return keys;
}

function sectionByKey(form: IntakeForm, key: string): IntakeFormSection | undefined {
  const raw = form.sections as unknown as Record<string, IntakeFormSection> | undefined;
  if (raw?.[key]) {
    return raw[key];
  }
  const listed = raw
    ? Object.entries(raw).find(([mapKey, section]) => section && (section.key || mapKey) === key)
    : undefined;
  if (listed) {
    return listed[1];
  }
  return (form.custom_sections ?? []).find(section => (section.key ?? section.title) === key);
}

function hiddenCopy(form: IntakeForm, key: string): CustomField | undefined {
  for (const section of sectionsOf(form)) {
    const found = (section.fields ?? []).find(field => field.field === key && field.hide_from_intake);
    if (found) {
      return found;
    }
  }
  return undefined;
}

function stripHiddenCopies(form: IntakeForm, key: string, keep: IntakeFormSection): void {
  sectionsOf(form).forEach(section => {
    if (section === keep || !section.fields) {
      return;
    }
    section.fields = section.fields.filter(field => !(field.field === key && field.hide_from_intake));
  });
}

/**
 * Copy a custom field the firm just placed into the section the sidebar reads.
 *
 * Creating a field writes the org catalog, and the server also files a hidden copy under Internal
 * Fields. Placing it on the v2 canvas writes a `field_key` onto a row and nothing else — `applyPages`
 * does not touch `fields`, and publish promotes that draft verbatim. The eviction sidebar and the
 * inbox list `section.fields`, so a value stored under `custom_fields` still has nowhere to render
 * until the placed section carries the definition.
 *
 * A field already listed on a section is left where it is. That is every migrated field.
 * Headings, dividers and rich text are not fields and are never copied.
 */
export function includePlacedCustomFields(
  form: IntakeForm,
  pages: LayoutPageView[],
  orgCustomFields: CustomField[] = []
): IntakeForm {
  const catalog = new Map(orgCustomFields.filter(field => field.field).map(field => [field.field, field]));

  // Where each field is placed *now*: the first row that carries it, and the section that row is in.
  const placedIn = new Map<string, IntakeFormSection>();
  const onCanvas = new Set<IntakeFormSection>();
  pages.forEach(page =>
    page.sections.forEach(view => {
      const target = sectionByKey(form, view.key);
      if (!target) {
        return;
      }
      onCanvas.add(target);
      view.rows.forEach(row =>
        row.items.forEach(item => {
          if (item.kind === 'FIELD' && item.field_key && !placedIn.has(item.field_key)) {
            placedIn.set(item.field_key, target);
          }
        })
      );
    })
  );

  placedIn.forEach((target, key) => {
    // Built-ins are listed from `built_in_fields`, never copied.
    if (sectionsOf(form).some(section => (section.built_in_fields ?? []).some(field => field.field === key))) {
      return;
    }
    // The visible copy wherever it sits now. A hidden one is the Internal Fields filing, not a listing.
    const holder = sectionsOf(form).find(section =>
      (section.fields ?? []).some(field => field.field === key && !field.hide_from_intake)
    );
    if (holder === target) {
      return;
    }
    const moved = holder?.fields?.find(field => field.field === key);
    const source = moved ?? catalog.get(key) ?? hiddenCopy(form, key);
    if (!source) {
      return;
    }
    if (holder && moved) {
      // The row moved to another section: the sidebar and inbox list `section.fields`, so the copy follows it.
      holder.fields = holder.fields.filter(field => field !== moved);
    }
    target.fields = target.fields ?? [];
    const existing = target.fields.find(field => field.field === key);
    if (existing) {
      existing.hide_from_intake = false;
    } else {
      target.fields.push({ ...source, hide_from_intake: false, order: target.fields.length });
    }
    stripHiddenCopies(form, key, target);
  });

  // A custom field whose row was taken off the canvas is no longer on the form: return its copy to the
  // hidden state the server files a new field in, rather than leaving it listed on a section it is not on.
  // Only sections the canvas draws, and only custom fields — a built-in or a never-placeable key is not
  // something removing a row can un-list.
  onCanvas.forEach(section =>
    (section.fields ?? []).forEach(field => {
      if (
        !field.hide_from_intake &&
        !field.built_in_field &&
        isIntakePlaceable(field.field) &&
        !COMPOSITE_PART_KEYS.has(field.field) &&
        !placedIn.has(field.field) &&
        section.layout_version === LAYOUT_VERSION_ROWS
      ) {
        field.hide_from_intake = true;
      }
    })
  );

  return form;
}

/**
 * The registry, as the picker and the inspector need it. Built from the form's own fields plus the org's
 * custom fields, so a field that exists but is not placed still shows up in the picker.
 */
export function toDefinitions(form: IntakeForm, orgCustomFields: CustomField[] = []): FieldDefinitionView[] {
  const seen = new Map<string, FieldDefinitionView>();

  /*
   * The declared sections first.
   *
   * Their questions live in wizard markup rather than in the registry, so without this the preview had
   * no definition to render from and fell through to a bare text box for every one of them — a preview
   * that does not look like the form it is previewing.
   *
   * First, so a registry field of the same key cannot overwrite what the wizard actually draws; that is
   * the same mistake that had Business Information asking for "Plaintiff Name".
   */
  const formOptions = (form.options ?? {}) as unknown as Record<string, { options?: string[] }>;
  // Radio vs checkboxes, the firm's labels, and the default — one derivation, shared with the legacy form.
  const evictionReason = evictionReasonFieldFromOptions(form.options);
  PUBLIC_FORM_SECTIONS.flatMap(section => section.rows.flat()).forEach(cell => {
    const reason = cell.key === 'eviction_reason' ? evictionReason : undefined;
    const narrowed = formOptions[cell.option ?? cell.key]?.options;
    seen.set(cell.key, {
      key: cell.key,
      label: cell.label,
      type: reason?.type ?? cell.type ?? CustomFieldType.TEXT,
      // The firm's own choice list where it set one, so the preview draws the control it will render and
      // the migration carries the configuration rather than silently reverting to the defaults.
      options: reason?.options?.length ? reason.options : narrowed?.length ? narrowed : cell.options,
      size_hint: sizeHintForColumns(undefined),
      origin: 'BUILT_IN',
      smart: false,
      // Carried through so the canvas and the preview know to draw the block rather than one control,
      // and so the picker places it as a unit.
      composite: cell.composite,
      /*
       * Both halves of the choice list, where the wizard has one.
       *
       * `options` above is the *effective* list; these say what the product's own is and where a
       * narrowed one is stored, which is what lets the placement panel edit it and offer a reset that
       * keeps following the product rather than freezing a copy of today's list.
       */
      standard_options: cell.options,
      option_key: cell.options ? (cell.option ?? cell.key) : undefined,
      // Labels, choice layout and the default live on the field. Without it ee-field would draw the
      // values and a checkbox firm would be a dropdown.
      ...(reason ? { field: reason } : {})
    });
  });
  // A brand-new custom field is filed under Internal Fields with `hide_from_intake`, which keeps it
  // out of the picker. Once it is also sitting on a row, that hidden copy is the only definition the
  // public form has — the catalog endpoint is attorney-only — and skipping it is how the wizard draws
  // a plain text box and files the answer outside `custom_fields`.
  const placed = placedFieldKeys(form);

  const add = (field: CustomField, origin: 'BUILT_IN' | 'CUSTOM') => {
    if (!field.field || seen.has(field.field)) return;
    // Never offered, so the picker cannot place one — see NEVER_ON_INTAKE.
    if (!isIntakePlaceable(field.field)) return;
    if (field.hide_from_intake && !placed.has(field.field)) return;
    /*
     * A part of a composite is not placeable on its own. The org record holds `property_city` and the
     * rest as ordinary custom fields — that is how v1 stored them — so without this the picker would
     * offer six controls the address autocomplete overwrites on the client's first keystroke.
     */
    if (COMPOSITE_PART_KEYS.has(field.field)) return;
    seen.set(field.field, {
      key: field.field,
      label: field.label ?? field.field,
      type: field.type,
      options: field.options,
      hint: field.hint,
      size_hint: sizeHintForColumns(field.columns),
      origin: field.built_in_field ? 'BUILT_IN' : origin,
      smart: field.type === CustomFieldType.CALCULATED || field.type === CustomFieldType.DATE_OFFSET,
      monetary: field.monetary ?? undefined,
      // The source field travels with the view so ee-field draws everything the firm configured.
      field,
      include_in_total: field.include_in_total,
      subtract_from_total: field.subtract_from_total
    });
  };

  // Custom sections too: `includePlacedCustomFields` files the copy there when the row sits in one, and
  // a definition missing from this list is how the answer ends up outside `custom_fields`.
  sectionsOf(form).forEach(section => {
    (section.built_in_fields ?? []).forEach(field => add(field, 'BUILT_IN'));
    (section.fields ?? []).forEach(field => add(field, 'CUSTOM'));
  });
  orgCustomFields.forEach(field => add(field, 'CUSTOM'));

  return [...seen.values()];
}

/** Every row on the form, flattened — used for counts and for the picker's already-placed set. */
export function allRows(pages: LayoutPageView[]): LayoutRow[] {
  return pages.flatMap(page => page.sections.flatMap(section => section.rows));
}
